<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { useExtendedI18n } from '@/i18n'
import { safeFormat } from '@/modules/shared/utils/date'
import type { Stat } from '@/modules/shared/components/types'
import type { Money } from '@/modules/shared/utils/money'
import { VStats } from '@/modules/shared/components'
import { Trophy } from 'lucide-vue-next'
import CapitalSummary from './capital-summary.vue'
import DistributionSummary from './distribution-summary.vue'
import { useHiddenMetrics } from '@/modules/workspace/composables/use-hidden-metrics'

const props = withDefaults(
  defineProps<{
    skeleton: boolean
    investorCount: number
    totalCommitted: Money
    paidCalled: Money
    isClosed?: boolean
    isExited?: boolean
    showMetrics?: boolean
    showKpi?: boolean
    exitDate?: string
    exitType?: string | null
    grossProceeds?: Money
    initialValue: Money
    cashPosition: Money
    roi: number
    moic: number
    xirr?: number | null
    realizedGain: Money
    unrealizedGain: Money
    calledSettled: number
    calledPending: number
    calledTotal: number
    committedAmount: number
    managementFees: number
    otherFees: number
    fundsDeployed: number
    cashPositionAmount: number
    operatingIncome: number
    operatingExpenses: number
    interestPaid: number
    interestReceived: number
    distributedPaid: number
    distributedPending: number
    returnOfCapital: number
    preferredReturn: number
    profit: number
    carriedInterestAmount: number
    netDistributed: number
    distributedOtherFees: number
    recycledFunds: number
    hurdle?: number
    currency?: string
  }>(),
  { xirr: null, showMetrics: true, showKpi: true, hurdle: 0, currency: 'USD' },
)

const { n } = useExtendedI18n()
const { hidden } = useHiddenMetrics()

const slots = useSlots()
const hasTaskList = computed(() => !!slots['task-list'])
const hasDealCard = computed(() => !!slots['deal-card'])
const hasSummary = computed(() => !!slots['summary'])
const hasRightColumnExtras = computed(() => !!slots['right-column-extras'])
const showRightColumn = computed(
  () => hasSummary.value || hasDealCard.value || hasTaskList.value || hasRightColumnExtras.value,
)

const kpiStats = computed<Stat[]>(() => [
  {
    label: 'ROI',
    sublabel: 'return on investment',
    value: props.roi,
    type: 'percent',
    colspan: 1,
    visible: !hidden.value.roi,
  },
  { label: 'XIRR', sublabel: 'extended IRR', value: props.xirr ?? 0, type: 'percent', colspan: 1 },
  {
    label: 'Realized G/L',
    sublabel: 'distributions received',
    value: props.realizedGain,
    type: 'currency',
    colspan: 1,
  },
  {
    label: 'Unrealized G/L',
    sublabel: 'unrealized value change',
    value: props.unrealizedGain,
    type: 'currency',
    colspan: 1,
  },
])
</script>

<template>
  <div v-if="showKpi" class="mb-8">
    <VStats :skeleton="skeleton" :stats="kpiStats" :mobile-cols="2" />
  </div>

  <div class="flex flex-col gap-5 lg:flex-row">
    <!-- LEFT: Financial summaries -->
    <div v-if="showMetrics" class="min-w-0 space-y-5 lg:flex-1">
      <!-- Exit hero banner -->
      <div
        v-if="isExited"
        class="rounded-lg p-6 text-center sm:p-8"
        :style="{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0), linear-gradient(to right, #4FB8A9, #3783B0)',
          backgroundSize: '16px 16px, 100%',
        }"
      >
        <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white/30">
          <Trophy :size="24" class="text-white" />
        </div>
        <div v-if="grossProceeds" class="mt-4 text-3xl font-bold text-white sm:text-4xl">
          {{ n(grossProceeds, 'currency') }}
        </div>
        <div class="mt-4 text-sm font-semibold uppercase tracking-widest text-white/90">
          {{ exitType || '—' }}
        </div>
        <div class="text-sm text-white/70">{{ safeFormat(exitDate, 'MMM d, yyyy', '—') }}</div>
      </div>

      <CapitalSummary
        :calledPaid="calledSettled"
        :calledPending="calledPending"
        :totalCalled="calledTotal"
        :totalCommitted="committedAmount"
        :managementFees="managementFees"
        :otherFees="otherFees"
        :fundsDeployed="fundsDeployed"
        :cashPosition="cashPositionAmount"
        :operatingIncome="operatingIncome"
        :operatingExpenses="operatingExpenses"
        :interestPaid="interestPaid"
        :interestReceived="interestReceived"
        :currency="currency"
      />

      <DistributionSummary
        v-if="isClosed || isExited"
        :distributedPaid="distributedPaid"
        :distributedPending="distributedPending"
        :returnOfCapital="returnOfCapital"
        :preferredReturn="preferredReturn"
        :profit="profit"
        :carriedInterest="carriedInterestAmount"
        :netDistributed="netDistributed"
        :otherFees="distributedOtherFees"
        :recycledFunds="recycledFunds"
        :hurdle="hurdle"
        :currency="currency"
      />
    </div>

    <!-- RIGHT: Entity content, deal card, task list -->
    <div v-if="showRightColumn" class="min-w-0 space-y-5 lg:flex-1">
      <slot name="summary" />

      <slot name="right-column-extras" />

      <div v-if="hasDealCard" class="overview-card">
        <slot name="deal-card" />
      </div>

      <slot name="task-list" />
    </div>
  </div>
</template>
